import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { ZodIssue } from 'zod';
import {
  DISPLAY_NAME_MAX_LENGTH,
  DisplayNameSchema,
  type UserProfile,
} from '@ghostscreen/contracts';
import { apiErrorMessage } from '../../i18n';
import { formatPersonName } from '../../lib/people/personName';
import { useUpdateMyProfile } from '../../lib/profile/useUpdateMyProfile';
import { SettingsRow } from './SettingsRow';

type ProfileNameFormProps = {
  profile: UserProfile;
  onSaved: (message: string) => void;
};

const ERROR_ID = 'profile-name-error';

/** The schema's messages are English; show the same rule in the reader's language. */
function validationMessage(issue: ZodIssue, t: TFunction<'settings'>): string {
  switch (issue.code) {
    case 'too_small':
      return t('profile.name.required');
    case 'too_big':
      return t('profile.name.tooLong', { max: DISPLAY_NAME_MAX_LENGTH });
    default:
      return t('profile.name.oneLine');
  }
}

export function ProfileNameForm({ profile, onSaved }: ProfileNameFormProps) {
  const { t } = useTranslation(['settings', 'common']);
  const [draft, setDraft] = useState(profile.displayName ?? '');
  const updateProfile = useUpdateMyProfile();

  const parsed = DisplayNameSchema.safeParse(draft);
  const unchanged = parsed.success && parsed.data === profile.displayName;
  const firstIssue = !parsed.success ? parsed.error.issues[0] : undefined;
  const validationError =
    draft.length > 0 && firstIssue ? validationMessage(firstIssue, t) : null;
  const saveError = updateProfile.isError
    ? apiErrorMessage(updateProfile.error, t('profile.name.saveError'))
    : null;
  const error = validationError ?? saveError;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!parsed.success || unchanged) return;
    updateProfile.mutate(
      { displayName: parsed.data },
      {
        onSuccess: (saved) => {
          setDraft(saved.displayName ?? '');
          onSaved(t('profile.name.saved'));
        },
      },
    );
  };

  return (
    <form onSubmit={submit} noValidate>
      <SettingsRow
        title={t('profile.name.title')}
        description={t('profile.name.description', { email: profile.email })}
      >
        <div className="flex w-full min-w-0 flex-col gap-1.5 sm:w-auto">
          <div className="flex gap-2">
            <input
              value={draft}
              onChange={(event) => {
                setDraft(event.target.value);
                if (updateProfile.isError) updateProfile.reset();
              }}
              placeholder={formatPersonName(
                { displayName: null, email: profile.email },
                t('profile.name.label'),
              )}
              maxLength={DISPLAY_NAME_MAX_LENGTH}
              autoComplete="name"
              aria-label={t('profile.name.label')}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? ERROR_ID : undefined}
              disabled={updateProfile.isPending}
              className="min-w-0 flex-1 rounded-[10px] border px-3 py-2.5 text-sm disabled:opacity-50 sm:w-[260px] sm:flex-none"
              style={{
                color: 'var(--gs-ink)',
                background: 'var(--gs-field)',
                borderColor: error ? 'var(--gs-bad)' : 'var(--gs-line)',
                fontFamily: 'inherit',
              }}
            />
            <button
              type="submit"
              disabled={!parsed.success || unchanged || updateProfile.isPending}
              className="shrink-0 rounded-[10px] px-3.5 py-2 text-sm font-bold text-[var(--gs-accent-ink)] disabled:opacity-50"
              style={{ background: 'var(--gs-accent-solid)' }}
            >
              {updateProfile.isPending ? t('common:actions.saving') : t('common:actions.save')}
            </button>
          </div>
          {error ? (
            <p id={ERROR_ID} role="alert" className="m-0 text-[13px]" style={{ color: 'var(--gs-bad)' }}>
              {error}
            </p>
          ) : null}
        </div>
      </SettingsRow>
    </form>
  );
}
